import React from 'react';
import { Box, Paper, Typography, Grid, Divider, Chip, Tooltip, Button } from '@mui/material';
import {
    PanTool, HourglassEmpty, SentimentSatisfiedAlt, AssignmentTurnedIn,
    SelfImprovement, VolumeOff, CrisisAlert, Shuffle, Speed, Healing,
    TaskAlt, EmojiEvents, InfoOutlined, PictureAsPdf
} from '@mui/icons-material';
// Importación de jspdf (Asegúrate de tenerlo instalado: npm install jspdf)
import { jsPDF } from 'jspdf';

// --- Data Structures and Mappings ---

const competencyDetails = {
    CI: {
        name: "Control de Impulso",
        icon: <PanTool />,
        color: '#4285F4', // Google Blue
        objectives: {
            Pregunta1: { text: "Paciencia y Deliberación", icon: <HourglassEmpty />, code: "CI-1" },
            Pregunta2: { text: "Regulación Emocional en interacciones", icon: <SentimentSatisfiedAlt />, code: "CI-2" },
            Pregunta3: { text: "Planificación frente a la Acción impulsiva", icon: <AssignmentTurnedIn />, code: "CI-3" },
            Pregunta4: { text: "Compostura bajo crítica", icon: <SelfImprovement />, code: "CI-4" },
            Pregunta5: { text: "Autocontrol verbal y la precisión", icon: <VolumeOff />, code: "CI-5" },
        },
        detailedInterpretations: {
            "Dominante Excepcional": "El candidato demuestra una capacidad sobresaliente para la autorregulación. Es extremadamente paciente, delibera cuidadosamente antes de actuar y mantiene una compostura inquebrantable incluso en las situaciones más desafiantes. Su autocontrol es un modelo a seguir.",
            "Fortaleza Máxima": "Posee un control de impulso muy fuerte. Rara vez reacciona de forma precipitada, gestiona sus emociones eficazmente y prefiere la planificación detallada. Es un individuo muy considerado y metódico.",
            "Muy Alto": "El control de impulso es una fortaleza clara. Muestra paciencia, buena regulación emocional y una tendencia a la planificación. Puede manejar la crítica con calma y es consciente de la precisión en su comunicación.",
            "Alto/Funcional Fuerte": "Tiene un buen nivel de control de impulso. Generalmente es reflexivo y calmado, aunque en momentos de alta presión podría mostrar ligeras fluctuaciones. Es un recurso confiable en situaciones que requieren deliberación.",
            "Funcional / Promedio": "El control de impulso es adecuado para la mayoría de las situaciones. Es capaz de esperar y planificar, pero bajo estrés extremo o interrupciones constantes, su regulación emocional podría verse comprometida. Es funcional, pero no su rasgo más distintivo.",
            "Ligeramente Bajo": "El candidato puede tener momentos de impulsividad. Aunque intenta deliberar, a veces reacciona rápidamente. La crítica puede afectarle más de lo deseado y su planificación podría ser menos rigurosa en ocasiones.",
            "Bajo/Necesita Monitoreo": "El control de impulso es un área a desarrollar. Tiende a reaccionar con cierta impulsividad, especialmente bajo presión. La regulación emocional y la paciencia no son sus puntos fuertes, lo que puede llevar a decisiones apresuradas o respuestas emocionales.",
            "Vulnerabilidad Notable": "Muestra una tendencia significativa a la impulsividad. Las interrupciones o críticas pueden provocar reacciones de frustración o enojo. La planificación es a menudo secundaria a la acción inmediata, lo que puede generar errores.",
            "Vulnerabilidad Crítica": "El control de impulso es muy bajo. Reacciona con frecuencia de forma emocional y precipitada. Le cuesta esperar, deliberar y mantener la calma, lo que puede afectar negativamente sus interacciones y la calidad de su trabajo.",
            "Riesgo Extremo": "El candidato rechaza activamente el comportamiento de control de impulso. Es altamente reactivo, impulsivo y puede mostrar poca consideración por la planificación o la deliberación, lo que representa un riesgo significativo en roles que requieren estabilidad y calma."
        }
    },
    ME: {
        name: "Manejo de Estrés",
        icon: <CrisisAlert />,
        color: '#34A853', // Google Green
        objectives: {
            Pregunta6: { text: "Tolerancia a la Ambigüedad y Adaptabilidad", icon: <Shuffle />, code: "ME-1" },
            Pregunta7: { text: "Eficacia bajo Presión", icon: <Speed />, code: "ME-2" },
            Pregunta8: { text: "Resiliencia y Capacidad de Recuperación", icon: <Healing />, code: "ME-3" },
            Pregunta9: { text: "Afrontamiento Orientado a la Tarea", icon: <TaskAlt />, code: "ME-4" },
            Pregunta10: { text: "Afrontamiento como Desafío (en lugar de Amenaza)", icon: <EmojiEvents />, code: "ME-5" },
        },
        detailedInterpretations: {
            "Dominante Excepcional": "El candidato no solo maneja el estrés, sino que lo utiliza como catalizador para un rendimiento superior. Es excepcionalmente adaptable, mantiene la eficacia bajo cualquier presión y se recupera instantáneamente, viendo cada desafío como una oportunidad de crecimiento.",
            "Fortaleza Máxima": "Demuestra una capacidad sobresaliente para gestionar el estrés. Su rendimiento se mantiene alto incluso en condiciones extremas, se adapta con facilidad a los cambios y su resiliencia le permite recuperarse rápidamente de situaciones difíciles.",
            "Muy Alto": "El manejo del estrés es una competencia muy sólida. Es eficaz bajo presión, se adapta bien a los cambios y se enfoca en soluciones prácticas. Ve los calendarios apretados como motivadores, no como cargas.",
            "Alto/Funcional Fuerte": "Posee un buen manejo del estrés. Es adaptable y mantiene un rendimiento adecuado bajo presión. Se recupera bien de los días difíciles y tiende a enfocarse en la resolución de problemas.",
            "Funcional / Promedio": "El candidato maneja el estrés de forma funcional. Es capaz de adaptarse y mantener el rendimiento, pero periodos prolongados de alta presión podrían empezar a afectarle. Se recupera, pero no siempre de forma inmediata.",
            "Ligeramente Bajo": "El manejo del estrés es un área que requiere atención. Los cambios de última hora o el alto volumen de trabajo pueden generarle cierta incomodidad, y su recuperación de situaciones estresantes podría ser más lenta.",
            "Bajo/Necesita Monitoreo": "El candidato muestra dificultades para manejar el estrés. La presión o los cambios inesperados pueden impactar negativamente su rendimiento y su capacidad de recuperación. Tiende a ver los desafíos como amenazas.",
            "Vulnerabilidad Notable": "El manejo del estrés es una vulnerabilidad significativa. Su rendimiento disminuye notablemente bajo presión, le cuesta adaptarse a los cambios y la recuperación de situaciones estresantes es prolongada. Puede enfocarse en el problema más que en la solución.",
            "Vulnerabilidad Crítica": "El candidato es muy vulnerable al estrés. Se siente sobrecargado con facilidad, su rendimiento se ve seriamente afectado por la presión y tiene una baja capacidad de recuperación. Los calendarios apretados le desmotivan profundamente.",
            "Riesgo Extremo": "El candidato rechaza activamente situaciones de estrés y presión. Su capacidad para funcionar en entornos dinámicos o exigentes es mínima, lo que representa un riesgo muy alto para roles que implican cualquier nivel de incertidumbre o demanda."
        }
    }
};

// Puntos asignados por ranking (1 = menos parecido, 5 = más parecido)
const rankingToPoints: any = { "1": 1, "2": 2, "3": 3, "4": 4, "5": 5 };

const scoreToInterpretation = (rawScore: number) => {
    if (rawScore >= 24) return { level: "Dominante Excepcional", scale: 10, implication: "El rasgo es una fortaleza conductual innegable." };
    if (rawScore >= 21) return { level: "Fortaleza Máxima", scale: 9, implication: "La competencia es una cualidad sobresaliente." };
    if (rawScore >= 19) return { level: "Muy Alto", scale: 8, implication: "Comportamiento exhibido de manera consistente." };
    if (rawScore >= 17) return { level: "Alto/Funcional Fuerte", scale: 7, implication: "La competencia está por encima del promedio." };
    if (rawScore >= 15) return { level: "Funcional / Promedio", scale: 6, implication: "El rasgo es adecuado para la mayoría de los roles." };
    if (rawScore >= 13) return { level: "Ligeramente Bajo", scale: 5, implication: "Podría requerir un esfuerzo consciente en situaciones clave." };
    if (rawScore >= 11) return { level: "Bajo/Necesita Monitoreo", scale: 4, implication: "El rasgo es recesivo y el candidato lo evita." };
    if (rawScore >= 8) return { level: "Vulnerabilidad Notable", scale: 3, implication: "Comportamiento inconsistente con lo que requiere la función." };
    if (rawScore >= 6) return { level: "Vulnerabilidad Crítica", scale: 2, implication: "El rasgo está casi ausente en las prioridades del candidato." };
    return { level: "Riesgo Extremo", scale: 1, implication: "El candidato rechaza activamente el comportamiento." };
};

const CircularScore = ({ score, color }: any) => (
    <Box sx={{ position: 'relative', display: 'inline-flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h3" fontWeight="bold" sx={{ color, position: 'absolute' }}>
            {score}
        </Typography>
        <Box sx={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            border: `8px solid ${color}30`,
        }}/>
    </Box>
);

const BehavioralPreferencesProfile = ({ responses }: any) => {

    const calculatedScores = React.useMemo(() => {
        const scores = { CI: 0, ME: 0 };
        // const rankingToPoints: any = { "1": 5, "2": 4, "3": 3, "4": 2, "5": 1 }; // Ya definida globalmente

        for (const question in responses) {
            const ranking = responses[question];
            const points = rankingToPoints[ranking] || 0;
            if (competencyDetails.CI.objectives.hasOwnProperty(question)) scores.CI += points;
            else if (competencyDetails.ME.objectives.hasOwnProperty(question)) scores.ME += points;
        }
        return scores;
    }, [responses]);

    const results = {
        CI: { ...competencyDetails.CI, score: calculatedScores.CI, interpretation: scoreToInterpretation(calculatedScores.CI) },
        ME: { ...competencyDetails.ME, score: calculatedScores.ME, interpretation: scoreToInterpretation(calculatedScores.ME) }
    };

    // --- Función de generación de PDF ---
    const generatePDF = () => {
        const doc = new jsPDF();
        let y = 15; // Posición inicial Y

        doc.setFontSize(18);
        doc.text("Perfil de Preferencias Conductuales", 105, y, { align: 'center' });
        y += 10;

        doc.setFontSize(10);
        doc.text("Reporte Detallado basado en Ordenamiento Forzado (Ipsativo)", 105, y, { align: 'center' });
        y += 15;

        // Iterar sobre las competencias
        Object.values(results).forEach((competency: any) => {
            // Sección de Competencia
            doc.setFontSize(16);
            doc.setTextColor(competency.color);
            doc.text(`${competency.name}`, 15, y);
            y += 8;

            doc.setFontSize(12);
            doc.setTextColor(0); // Negro
            doc.text(`Puntuación Final (1-10): ${competency.interpretation.scale}`, 15, y);
            doc.text(`Clasificación: ${competency.interpretation.level}`, 80, y);
            doc.text(`Puntuación Bruta: ${competency.score}/25`, 150, y);
            y += 8;

            doc.line(15, y, 195, y); // Separador
            y += 8;

            // Interpretación Detallada
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text("Análisis Detallado:", 15, y);
            doc.setFont("helvetica", "normal");
            y += 5;
            const detailedText = doc.splitTextToSize(competency.detailedInterpretations[competency.interpretation.level], 170);
            doc.text(detailedText, 15, y);
            y += detailedText.length * 5 + 4;

            doc.setFont("helvetica", "bold");
            doc.text("Implicación General:", 15, y);
            doc.setFont("helvetica", "normal");
            doc.text(competency.interpretation.implication, 55, y);
            y += 10;

            doc.setFontSize(14);
            doc.setTextColor(0); // Negro
            doc.setFont("helvetica", "bold");
            doc.text("Desglose por Ítem de Medición", 15, y);
            doc.setFont("helvetica", "normal");
            y += 8;

            // Tabla de Desglose
            doc.setFontSize(10);
            const headers = ["Ítem", "Objetivo de la Medición", "Ranking (1-5)", "Puntos"];
            let tableY = y;
            const cellMargin = 5;
            const startX = 15;
            const colWidths = [15, 120, 20, 20];
            let currentX = startX;

            // Dibujar encabezados
            doc.setFont("helvetica", "bold");
            doc.setFillColor(200, 220, 255);
            doc.rect(startX, tableY, colWidths.reduce((a, b) => a + b), 6, 'F');
            currentX = startX;
            headers.forEach((header, index) => {
                doc.text(header, currentX + cellMargin, tableY + 4);
                currentX += colWidths[index];
            });
            tableY += 6;
            doc.setFont("helvetica", "normal");

            // Dibujar filas de datos
            Object.values(competency.objectives).forEach((objective: any, index: number) => {
                const questionKey = Object.keys(competency.objectives)[index];
                const ranking = responses[questionKey];
                const points = rankingToPoints[ranking] || 0;

                // Verificar si se necesita una nueva página
                if (tableY > 280) {
                    doc.addPage();
                    tableY = 20; // Nueva posición inicial Y
                }

                currentX = startX;
                doc.rect(startX, tableY, colWidths.reduce((a, b) => a + b), 6, 'S'); // Borde de la fila

                doc.text(objective.code, currentX + cellMargin, tableY + 4);
                currentX += colWidths[0];

                const objectiveText = doc.splitTextToSize(objective.text, colWidths[1] - (2 * cellMargin));
                doc.text(objectiveText, currentX + cellMargin, tableY + 4);
                currentX += colWidths[1];

                doc.text(String(ranking), currentX + cellMargin, tableY + 4);
                currentX += colWidths[2];

                doc.text(String(points), currentX + cellMargin, tableY + 4);
                currentX += colWidths[3];

                tableY += 6;
            });

            y = tableY + 10; // Actualizar posición Y para la siguiente competencia
        });


        // Pie de página
        doc.setFontSize(8);
        doc.text("Generado por el Sistema de Evaluación de Preferencias Conductuales", 105, 290, { align: 'center' });

        doc.save("Reporte_Preferencias_Conductuales.pdf");
    };
    // --- Fin de la Función de generación de PDF ---

    return (
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, backgroundColor: '#f5f5f5' }}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 4, backgroundColor: 'white' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1" align="left" fontWeight="bold" sx={{ color: '#202124' }}>
                        Perfil de Preferencias Conductuales
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<PictureAsPdf />}
                        onClick={generatePDF}
                        sx={{
                            backgroundColor: '#D93025', // Google Red para PDF
                            '&:hover': { backgroundColor: '#B3261F' },
                            fontWeight: 'bold'
                        }}
                    >
                        Descargar PDF
                    </Button>
                </Box>

                <Paper variant="outlined" sx={{ p: 2, mt: 3, mb: 5, borderRadius: 3, backgroundColor: '#f0f4f8', borderColor: '#dfe3e8' }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: '600', mb: 1 }}>
                        <InfoOutlined sx={{ mr: 1, color: 'primary.main' }} />
                        Guía de Lectura del Reporte
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        El candidato ordenó las frases del <strong>1 (Menos parecido a mí)</strong> al <strong>5 (Más parecido a mí)</strong>. Con base en sus respuestas, el sistema calcula los siguientes indicadores:
                    </Typography>
                    <ul style={{ margin: '8px 0 0 20px', padding: 0, fontSize: '0.875rem', color: '#5f6368' }}>
                        <li><strong>Puntuación Bruta (5-25):</strong> Es la suma directa de los puntos asignados por el candidato. Refleja el nivel de prioridad o importancia que el evaluado le otorga a cada competencia.</li>
                        <li><strong>Puntuación Final (1-10):</strong> Es la conversión de la puntuación bruta a una escala estándar de 1 a 10, diseñada para ofrecerte una interpretación visual y una comparativa rápida del perfil.</li>
                    </ul>
                </Paper>

                <Grid container spacing={5}>
                    {Object.values(results).map((competency: any) => (
                        <Grid item xs={12} md={6} key={competency.name}>
                            <Paper variant="outlined" sx={{ borderRadius: 4, p: 3, height: '100%', borderColor: `${competency.color}50` }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    {React.cloneElement(competency.icon, { style: { color: competency.color, fontSize: 32, marginRight: 12 } })}
                                    <Typography variant="h5" fontWeight="600" sx={{ color: competency.color }}>{competency.name}</Typography>
                                </Box>

                                <Box sx={{ textAlign: 'center', my: 4 }}>
                                    <CircularScore score={competency.interpretation.scale} color={competency.color} />
                                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                                        {competency.interpretation.level}
                                    </Typography>
                                    <Typography variant="subtitle1" color="text.secondary">
                                        Puntuación Bruta: {competency.score}/25
                                    </Typography>
                                </Box>

                                <Typography variant="h6" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>Análisis Detallado</Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 3,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        component="div"
                                        sx={{ lineHeight: 1.7, margin: 0 }}
                                    >
                                        {competency.detailedInterpretations[competency.interpretation.level]}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        component="div"
                                        sx={{ fontStyle: 'italic', margin: 0 }}
                                    >
                                        <strong>Implicación General:</strong> {competency.interpretation.implication}
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Desglose de Puntuación</Typography>
                                {Object.keys(competency.objectives).map((questionKey: any) => (
                                    <Box key={questionKey} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        {React.cloneElement(competency.objectives[questionKey].icon, { style: { color: '#5f6368', marginRight: 12 } })}
                                        <Typography variant="body1" sx={{ flexGrow: 1 }}>{competency.objectives[questionKey].text}</Typography>
                                        <Tooltip title="Ranking del candidato en una escala del 1 al 5 (1 = Menos parecido a mí, 5 = Más parecido a mí)">
                                            <Chip label={`Ranking: ${responses[questionKey]} / 5`} size="small" sx={{ fontWeight: '500' }} />
                                        </Tooltip>
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        </Box>
    );
};

export default BehavioralPreferencesProfile;
